import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useCardsQuery, useCoachBuildMutation, useSaveDeckMutation } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { Paywall } from '@/components/cr/Paywall';
import { buildCardIndex, resolveDeckNames } from '@/lib/crCardIndex';
import { coachAnalysisError } from '@/components/cr/coachError';
import { saveDeckErrorKey } from './saveDeckError';
import { ImportPill, SavePill } from './DeckActions';

/**
 * The AI deck builder, as a sheet over the panel rather than a route.
 *
 * It lives inside the Hub because leaving the Hub is what made this feature
 * unreachable in the first place: /cr/decks/builder has had no inbound link
 * since the tile that pointed at it was deleted.
 *
 * The AI returns card NAMES; the catalog turns them into cards, which is what
 * makes the result playable (import) instead of a list of words.
 */
export function DeckBuilderSheet({ isPro, onClose }: { isPro: boolean; onClose: () => void }) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [style, setStyle] = useState('');
  const [build, { data, isLoading, error, reset }] = useCoachBuildMutation();
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: catalog } = useCardsQuery();
  const index = useMemo(() => buildCardIndex(catalog), [catalog]);
  const built = data?.deck;
  const resolved = useMemo(
    () => (built ? resolveDeckNames(built.cards, index) : null),
    [built, index],
  );

  const canSubmit = style.trim().length > 0 && !isLoading;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    haptics.impact('medium');
    reset();
    setSaved(false);
    setSaveError(null);
    build({ style: style.trim() }).catch(() => undefined);
  };

  const onSave = async () => {
    if (!built || built.cards.length < 8) return;
    setSaveError(null);
    try {
      await saveDeck({
        name: t('panel.builder.saveName', { archetype: built.archetype }),
        cards: built.cards.slice(0, 8),
      }).unwrap();
      haptics.notification('success');
      setSaved(true);
    } catch (err) {
      haptics.notification('error');
      setSaveError(t(saveDeckErrorKey(err)));
    }
  };

  return (
    <div className="absolute inset-0 z-10 flex flex-col gap-3 overflow-y-auto overflow-x-hidden overscroll-contain bg-bg pb-2">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand">
          <Sparkles className="h-3.5 w-3.5" />
          {t('builder.title')}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('panel.close')}
          className="rounded-md p-1 text-fg-muted transition-opacity active:opacity-70"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {!isPro ? (
        <Paywall feature={t('builder.title')} />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-2">
          <input
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            placeholder={t('builder.field.placeholder')}
            maxLength={120}
            className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
          />
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-lg bg-brand py-2 text-[11px] font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80 disabled:opacity-40"
          >
            {isLoading
              ? t('builder.thinking')
              : data
                ? t('builder.cta.regenerate')
                : t('builder.cta.generate')}
          </button>
          {/* 5 builds an hour — the tightest bucket in the CR namespace, and its
              own (it doesn't share with the coach). Worth saying out loud
              before the tap, not after the 429. */}
          <p className="text-center text-[10px] text-fg-muted">{t('panel.builder.quota')}</p>

          {!!error && (
            <p className="rounded-lg bg-rose-500/10 p-2.5 text-[11px] text-rose-300">
              {coachAnalysisError(error, t)}
            </p>
          )}

          {built && resolved && (
            <div className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-bold text-brand">{built.archetype}</span>
                <span className="shrink-0 text-[10px] text-fg-muted">⚡ {built.avgElixir}</span>
              </div>

              {resolved.cards.length > 0 && <DeckGrid cards={resolved.cards} showLevels={false} />}
              {resolved.unresolved.length > 0 && (
                <ul className="flex flex-wrap gap-1">
                  {resolved.unresolved.map((name) => (
                    <li
                      key={name}
                      className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] text-fg-muted"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}

              <p className="whitespace-pre-line text-[11px] leading-snug text-fg-muted">
                {built.strategy}
              </p>

              <div className="grid grid-cols-2 gap-1.5">
                <ImportPill cards={resolved.cards} label={t('panel.action.import')} />
                <SavePill
                  saved={saved}
                  saving={saving}
                  onSave={onSave}
                  label={t('panel.action.save')}
                  savedLabel={t('panel.action.saved')}
                />
              </div>
              {saveError && <p className="text-[11px] text-rose-300">{saveError}</p>}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
